import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertTriangle, Clock, MapPin, ChevronRight, X, Users, Calendar } from 'lucide-react';
import { CustomSelect } from '../common/CustomSelect';
import { CustomTimePicker } from '../common/CustomTimePicker';

// ============================================================================
// TIMECARD REVIEW MODAL
// Displays on Friday clock-in to allow employees to review their weekly time
// ============================================================================

interface TimeEntry {
  entry_id: number;
  entry_date: string;
  day_name: string;
  clock_in: string;
  clock_out: string;
  job_id: number;
  job_name: string;
  bradanna_job_number: string;
  hours_worked: number;
  labor_code: string;
  lunch_taken: boolean;
  lunch_minutes: number;
  needs_review: boolean;
  has_pending_correction: boolean;
}

interface Correction {
  entry_id: number | null;
  correction_type: 'wrong_clock_in' | 'wrong_clock_out' | 'wrong_job' | 'missed_day' | 'did_not_take_lunch';
  proposed_clock_in?: string;
  proposed_clock_out?: string;
  proposed_job_id?: number;
  proposed_date?: string;
  employee_notes: string;
  auto_fix?: boolean; // True if this was auto-generated to fix overlap
}

// A change request already on file for this week (from My Time or an earlier
// pass through this modal), with the office's decision so far.
interface FiledRequest {
  correction_id: number;
  entry_id: number | null;
  correction_type: 'wrong_clock_in' | 'wrong_clock_out' | 'wrong_job' | 'missed_day' | 'did_not_take_lunch';
  proposed_clock_in?: string | null;
  proposed_clock_out?: string | null;
  proposed_job_id?: number | null;
  proposed_job_name?: string | null;
  proposed_date?: string | null;
  employee_notes?: string | null;
  admin_status: 'pending' | 'approved' | 'rejected' | 'modified' | string;
  admin_notes?: string | null;
}

interface TimecardReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApprove: () => void;
  onSkip: () => void;
  weekStartDate: string;
  weekEndDate: string;
  entries: TimeEntry[];
  filedRequests?: FiledRequest[];
  crewAverageClockOut: string;
  totalHours: string;
  canSkip: boolean;
  language: 'en' | 'es';
  apiCall: (endpoint: string, method?: string, body?: any) => Promise<any>;
  jobs: Array<{ job_id: number; job_name: string; bradanna_job_number: string }>;
}

// Translation system
const translations = {
  en: {
    // Modal titles
    reviewTitle: 'Review Your Time',
    reviewSubtitle: 'Please review your hours from last week',
    selectIssuesTitle: 'Select Days With Issues',
    selectIssuesSubtitle: 'Which days have problems?',
    correctionTitle: 'Propose Correction',
    confirmTitle: 'Confirm Corrections',
    successTitle: 'Thank You!',

    // Week display
    weekOf: 'Week of',
    totalHours: 'Total Hours',
    noEntries: 'No time entries for this week',

    // Day names
    friday: 'Friday',
    saturday: 'Saturday',
    sunday: 'Sunday',
    monday: 'Monday',
    tuesday: 'Tuesday',
    wednesday: 'Wednesday',
    thursday: 'Thursday',

    // Entry display
    hours: 'hrs',
    lunchDeducted: 'lunch deducted',
    noClockOut: 'No clock out',

    // Buttons
    allTimeCorrect: 'All Time is Correct',
    somethingWrong: 'Something is Wrong',
    skipForNow: 'Skip for Now',
    continue: 'Continue',
    back: 'Back',
    submit: 'Submit Corrections',
    done: 'Done',
    cancel: 'Cancel',
    save: 'Save',
    next: 'Next',
    nothingWrong: 'Nothing wrong with this day',
    noMissedDay: "I didn't miss a day",

    // Issue selection
    selectAtLeastOne: 'Select at least one day',
    missedDayOption: 'I missed a day (no entry exists)',
    noLunchOption: 'I did not take lunch',
    selectedCount: 'selected',

    // Correction types
    whatIsWrong: "What's wrong?",
    wrongClockIn: 'Wrong clock-in time',
    wrongClockOut: 'Wrong clock-out time',
    wrongJob: 'Wrong job assignment',
    missedDay: 'Missed day (no entry)',
    didNotTakeLunch: 'Did not take lunch',

    // Correction form
    currentValue: 'Current',
    correctValue: 'Correct Value',
    useCrewAverage: 'Use Crew Average',
    crewAverage: 'Crew Average',
    enterManually: 'Enter Manually',
    selectJob: 'Select Job',
    selectDate: 'Select Date',
    addNotes: 'Add notes (optional)',
    notesPlaceholder: 'Explain what happened...',

    // Confirmation
    correctionsToSubmit: 'Corrections to Submit',
    original: 'Original',
    proposed: 'Proposed',
    yourNote: 'Your Note',

    // Success
    correctionsSubmitted: 'Your corrections have been submitted for admin review.',
    timeApproved: 'Your time has been approved. Thank you!',

    // Validation
    pleaseSelectType: 'Please select what is wrong',
    pleaseEnterTime: 'Please enter the correct time',
    pleaseSelectJob: 'Please select the correct job',
    pleaseSelectDate: 'Please select the date you worked',

    // Status
    pendingCorrection: 'Pending correction',
    filedTitle: 'Changes you already requested',
    reqPending: 'Waiting for office', reqApproved: 'Approved', reqDenied: 'Denied', reqAlready: 'Already requested (see below) — edit it in My Time',
    managerNote: 'Manager note', reqClockIn: 'Clock-in', reqClockOut: 'Clock-out', reqJob: 'Job', reqNoLunch: 'No lunch taken', reqMissed: 'Missed day',
    flaggedForReview: 'Flagged for review'
  },
  es: {
    // Modal titles
    reviewTitle: 'Revise Su Tiempo',
    reviewSubtitle: 'Por favor revise sus horas de la semana pasada',
    selectIssuesTitle: 'Seleccione Días con Problemas',
    selectIssuesSubtitle: '¿Cuáles días tienen problemas?',
    correctionTitle: 'Proponer Corrección',
    confirmTitle: 'Confirmar Correcciones',
    successTitle: '¡Gracias!',

    // Week display
    weekOf: 'Semana del',
    totalHours: 'Horas Totales',
    noEntries: 'No hay entradas de tiempo para esta semana',

    // Day names
    friday: 'Viernes',
    saturday: 'Sábado',
    sunday: 'Domingo',
    monday: 'Lunes',
    tuesday: 'Martes',
    wednesday: 'Miércoles',
    thursday: 'Jueves',

    // Entry display
    hours: 'hrs',
    lunchDeducted: 'almuerzo descontado',
    noClockOut: 'Sin salida',

    // Buttons
    allTimeCorrect: 'Todo el Tiempo es Correcto',
    somethingWrong: 'Algo Está Mal',
    skipForNow: 'Omitir por Ahora',
    continue: 'Continuar',
    back: 'Atrás',
    submit: 'Enviar Correcciones',
    done: 'Listo',
    cancel: 'Cancelar',
    save: 'Guardar',
    next: 'Siguiente',
    nothingWrong: 'Nada mal con este día',
    noMissedDay: 'No falté ningún día',

    // Issue selection
    selectAtLeastOne: 'Seleccione al menos un día',
    missedDayOption: 'Falté un día (no hay entrada)',
    noLunchOption: 'No tomé almuerzo',
    selectedCount: 'seleccionados',

    // Correction types
    whatIsWrong: '¿Qué está mal?',
    wrongClockIn: 'Hora de entrada incorrecta',
    wrongClockOut: 'Hora de salida incorrecta',
    wrongJob: 'Trabajo asignado incorrecto',
    missedDay: 'Día perdido (sin entrada)',
    didNotTakeLunch: 'No tomó almuerzo',

    // Correction form
    currentValue: 'Actual',
    correctValue: 'Valor Correcto',
    useCrewAverage: 'Usar Promedio del Equipo',
    crewAverage: 'Promedio del Equipo',
    enterManually: 'Ingresar Manualmente',
    selectJob: 'Seleccionar Trabajo',
    selectDate: 'Seleccionar Fecha',
    addNotes: 'Agregar notas (opcional)',
    notesPlaceholder: 'Explique qué pasó...',

    // Confirmation
    correctionsToSubmit: 'Correcciones a Enviar',
    original: 'Original',
    proposed: 'Propuesto',
    yourNote: 'Su Nota',

    // Success
    correctionsSubmitted: 'Sus correcciones han sido enviadas para revisión del administrador.',
    timeApproved: '¡Su tiempo ha sido aprobado. Gracias!',

    // Validation
    pleaseSelectType: 'Por favor seleccione qué está mal',
    pleaseEnterTime: 'Por favor ingrese la hora correcta',
    pleaseSelectJob: 'Por favor seleccione el trabajo correcto',
    pleaseSelectDate: 'Por favor seleccione la fecha que trabajó',

    // Status
    pendingCorrection: 'Corrección pendiente',
    filedTitle: 'Cambios que ya solicitó',
    reqPending: 'Esperando a la oficina', reqApproved: 'Aprobado', reqDenied: 'Denegado', reqAlready: 'Ya solicitado (vea abajo) — edítelo en Mi Tiempo',
    managerNote: 'Nota del gerente', reqClockIn: 'Entrada', reqClockOut: 'Salida', reqJob: 'Trabajo', reqNoLunch: 'Sin almuerzo', reqMissed: 'Día faltante',
    flaggedForReview: 'Marcado para revisión'
  }
};

// Styles
const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.6)',
    display: 'flex',
    alignItems: 'flex-end',
    zIndex: 1000
  },
  modal: {
    backgroundColor: 'white',
    borderTopLeftRadius: '24px',
    borderTopRightRadius: '24px',
    width: '100%',
    maxHeight: '90vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    animation: 'slideUp 0.3s ease-out'
  },
  header: {
    background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
    color: 'white',
    padding: '20px',
    textAlign: 'center'
  },
  headerTitle: {
    fontSize: '20px',
    fontWeight: '700',
    margin: 0
  },
  headerSubtitle: {
    fontSize: '14px',
    opacity: 0.9,
    marginTop: '4px'
  },
  weekBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    backgroundColor: 'rgba(255,255,255,0.2)',
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    marginTop: '10px'
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px'
  },
  totalCard: {
    backgroundColor: '#f0fdf4',
    border: '2px solid #10b981',
    borderRadius: '12px',
    padding: '16px',
    textAlign: 'center',
    marginBottom: '16px'
  },
  totalLabel: {
    fontSize: '12px',
    color: '#065f46',
    fontWeight: '600',
    textTransform: 'uppercase'
  },
  totalValue: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#10b981'
  },
  entryCard: {
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    padding: '14px',
    marginBottom: '10px'
  },
  entryHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px'
  },
  entryDay: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#1e293b'
  },
  entryHours: {
    fontSize: '15px',
    fontWeight: '700',
    color: '#10b981'
  },
  entryJob: {
    fontSize: '13px',
    color: '#64748b',
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },
  entryTimes: {
    fontSize: '12px',
    color: '#94a3b8',
    marginTop: '6px',
    display: 'flex',
    gap: '12px'
  },
  lunchBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    backgroundColor: '#fef3c7',
    color: '#92400e',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    marginTop: '6px'
  },
  flagBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    marginTop: '6px',
    marginLeft: '6px'
  },
  footer: {
    padding: '16px',
    paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
    borderTop: '1px solid #e2e8f0',
    backgroundColor: 'white'
  },
  buttonPrimary: {
    width: '100%',
    padding: '16px',
    backgroundColor: '#10b981',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    marginBottom: '10px'
  },
  buttonSecondary: {
    width: '100%',
    padding: '16px',
    backgroundColor: 'white',
    color: '#f59e0b',
    border: '2px solid #f59e0b',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    marginBottom: '10px'
  },
  buttonSkip: {
    width: '100%',
    padding: '12px',
    backgroundColor: 'transparent',
    color: '#94a3b8',
    border: 'none',
    fontSize: '14px',
    cursor: 'pointer'
  },
  checkboxCard: {
    backgroundColor: '#f8fafc',
    border: '2px solid #e2e8f0',
    borderRadius: '12px',
    padding: '14px',
    marginBottom: '10px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    cursor: 'pointer'
  },
  checkboxCardSelected: {
    backgroundColor: '#fef3c7',
    border: '2px solid #f59e0b'
  },
  checkbox: {
    width: '24px',
    height: '24px',
    borderRadius: '6px',
    border: '2px solid #cbd5e1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  checkboxChecked: {
    backgroundColor: '#f59e0b',
    borderColor: '#f59e0b',
    color: 'white'
  },
  formGroup: {
    marginBottom: '16px'
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '8px'
  },
  select: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    backgroundColor: 'white'
  },
  input: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    boxSizing: 'border-box'
  },
  textarea: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    minHeight: '80px',
    resize: 'vertical',
    boxSizing: 'border-box'
  },
  crewAverageButton: {
    width: '100%',
    padding: '14px',
    backgroundColor: '#dcfce7',
    border: '2px solid #10b981',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    color: '#065f46',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    marginBottom: '12px'
  },
  orDivider: {
    textAlign: 'center',
    color: '#94a3b8',
    fontSize: '13px',
    margin: '12px 0'
  },
  currentValueBox: {
    backgroundColor: '#fee2e2',
    padding: '12px',
    borderRadius: '8px',
    marginBottom: '12px'
  },
  currentValueLabel: {
    fontSize: '12px',
    color: '#991b1b',
    fontWeight: '600'
  },
  currentValueText: {
    fontSize: '16px',
    color: '#dc2626',
    fontWeight: '600',
    textDecoration: 'line-through'
  },
  successIcon: {
    width: '80px',
    height: '80px',
    backgroundColor: '#dcfce7',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px'
  },
  summaryCard: {
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    padding: '14px',
    marginBottom: '12px'
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '8px'
  },
  summaryLabel: {
    fontSize: '12px',
    color: '#64748b',
    textTransform: 'uppercase'
  },
  summaryValue: {
    fontSize: '14px',
    fontWeight: '600'
  },
  noEntriesBox: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#64748b'
  }
};

// Add keyframes for animation
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes slideUp {
    from { transform: translateY(100%); }
    to { transform: translateY(0); }
  }
`;
if (!document.querySelector('#timecard-review-styles')) {
  styleSheet.id = 'timecard-review-styles';
  document.head.appendChild(styleSheet);
}

type ModalStep = 'review' | 'selectIssues' | 'correction' | 'confirm' | 'success';

const TimecardReviewModal: React.FC<TimecardReviewModalProps> = ({
  isOpen,
  onClose,
  onApprove,
  onSkip,
  weekStartDate,
  weekEndDate,
  entries,
  filedRequests = [],
  crewAverageClockOut,
  totalHours,
  canSkip,
  language,
  apiCall,
  jobs
}) => {
  const t = translations[language];

  const [step, setStep] = useState<ModalStep>('review');
  const [selectedEntries, setSelectedEntries] = useState<number[]>([]);
  const [includeMissedDay, setIncludeMissedDay] = useState(false);
  const [includeNoLunch, setIncludeNoLunch] = useState(false);
  const [isAsphaltCrew, setIsAsphaltCrew] = useState(false);
  const [corrections, setCorrections] = useState<Correction[]>([]);
  // Screens = one wizard page per flagged entry (+ one for a missed day). A single
  // screen can now produce MULTIPLE corrections (clock-in + clock-out + no-lunch...),
  // so navigation is keyed off screens while `corrections` holds the flat payload.
  const [screens, setScreens] = useState<Array<{ entry_id: number | null; missed_day: boolean }>>([]);
  const [currentScreenIndex, setCurrentScreenIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Per-day issue checkboxes (which problems the worker ticked on the current screen)
  const [issueClockIn, setIssueClockIn] = useState(false);
  const [issueClockOut, setIssueClockOut] = useState(false);
  const [issueWrongJob, setIssueWrongJob] = useState(false);
  const [issueNoLunch, setIssueNoLunch] = useState(false);

  // Current correction form state.
  //  proposedTime     -> regular clock-in value AND missed-day clock-in
  //  proposedClockOut -> regular clock-out value AND missed-day clock-out
  //  proposedJobId    -> regular wrong-job value AND missed-day job
  //  proposedDate     -> missed-day date
  const [proposedTime, setProposedTime] = useState('');
  const [proposedClockOut, setProposedClockOut] = useState('');
  const [proposedJobId, setProposedJobId] = useState<number | null>(null);
  const [proposedDate, setProposedDate] = useState('');
  const [notes, setNotes] = useState('');

  const draftKey = `timecard_review_draft_${weekStartDate}`;

  const resetAllState = () => {
    setStep('review');
    setSelectedEntries([]);
    setIncludeMissedDay(false);
    setIncludeNoLunch(false);
    setCorrections([]);
    setScreens([]);
    setCurrentScreenIndex(0);
    setError(null);
    setIssueClockIn(false);
    setIssueClockOut(false);
    setIssueWrongJob(false);
    setIssueNoLunch(false);
    setProposedTime('');
    setProposedClockOut('');
    setProposedJobId(null);
    setProposedDate('');
    setNotes('');
  };

  const clearDraft = () => {
    try { localStorage.removeItem(draftKey); } catch (e) {}
  };

  // Reset state when modal opens — restore from localStorage if a draft exists
  useEffect(() => {
    if (isOpen) {
      let restored = false;
      try {
        const raw = localStorage.getItem(draftKey);
        if (raw) {
          const draft = JSON.parse(raw);
          const age = Date.now() - (draft.timestamp || 0);
          if (age < 7 * 24 * 60 * 60 * 1000) {
            setStep(draft.step || 'review');
            setSelectedEntries(draft.selectedEntries || []);
            setIncludeMissedDay(draft.includeMissedDay || false);
            setIncludeNoLunch(draft.includeNoLunch || false);
            setCorrections(draft.corrections || []);
            setScreens(draft.screens || []);
            setCurrentScreenIndex(draft.currentScreenIndex || 0);
            setIssueClockIn(draft.issueClockIn || false);
            setIssueClockOut(draft.issueClockOut || false);
            setIssueWrongJob(draft.issueWrongJob || false);
            setIssueNoLunch(draft.issueNoLunch || false);
            setProposedTime(draft.proposedTime || '');
            setProposedClockOut(draft.proposedClockOut || '');
            setProposedJobId(draft.proposedJobId ?? null);
            setProposedDate(draft.proposedDate || '');
            setNotes(draft.notes || '');
            setError(null);
            restored = true;
          }
        }
      } catch (e) {
        // Corrupt or unreadable draft — ignore
      }

      if (!restored) {
        resetAllState();
      }

      // Check if employee is in asphalt crew
      const checkAsphaltCrew = async () => {
        try {
          const response = await apiCall('/api/timecard-review/is-asphalt-crew');
          if (response.success && response.data) {
            setIsAsphaltCrew(response.data.isAsphaltCrew);
          }
        } catch (err) {
          console.error('Failed to check asphalt crew status:', err);
          setIsAsphaltCrew(false);
        }
      };
      checkAsphaltCrew();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Persist wizard state to localStorage so back-button doesn't lose progress
  useEffect(() => {
    if (!isOpen || step === 'success') return;
    try {
      localStorage.setItem(draftKey, JSON.stringify({
        step,
        selectedEntries,
        includeMissedDay,
        includeNoLunch,
        corrections,
        screens,
        currentScreenIndex,
        issueClockIn,
        issueClockOut,
        issueWrongJob,
        issueNoLunch,
        proposedTime,
        proposedClockOut,
        proposedJobId,
        proposedDate,
        notes,
        timestamp: Date.now()
      }));
    } catch (e) {
      // Storage full or unavailable — degrade gracefully
    }
  }, [isOpen, step, selectedEntries, includeMissedDay, includeNoLunch, corrections, screens, currentScreenIndex, issueClockIn, issueClockOut, issueWrongJob, issueNoLunch, proposedTime, proposedClockOut, proposedJobId, proposedDate, notes, draftKey]);

  if (!isOpen) return null;

  // Format time for display (convert UTC to local)
  const formatTime = (isoString: string) => {
    if (!isoString) return '--:--';
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Format date for display - parse as local time to avoid timezone shift
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '--';
    // Handle different date formats
    let dateOnly = dateStr;
    // If it's an ISO string with time, extract just the date part
    if (dateStr.includes('T')) {
      dateOnly = dateStr.split('T')[0];
    }
    // Append T12:00:00 to treat date as local noon (avoids timezone shifting the date)
    const date = new Date(dateOnly + 'T12:00:00');
    if (isNaN(date.getTime())) return '--';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Convert a stored ISO/UTC timestamp to a local "HH:MM" string for the time
  // picker. Used to pre-fill correction fields with the worker's ACTUAL recorded
  // time so an unchanged value can be treated as "no correction needed".
  const isoToLocalHHMM = (isoString: string) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '';
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  };

  // All of a day's entries (sorted by clock-in) given any one entry on that day.
  // The Friday review is DAY-LEVEL: the worker fixes the day's overall clock-in
  // (first event) and clock-out (last event); mid-day job-switch times are not
  // editable here, so a worker can't create an overlap.
  const dayEntriesFor = (entryId: number | null) => {
    if (entryId == null) return [];
    const e = entries.find((x) => x.entry_id === entryId);
    if (!e) return [];
    const dk = e.entry_date?.split('T')[0];
    return entries
      .filter((x) => x.entry_date?.split('T')[0] === dk)
      .sort((a, b) => new Date(a.clock_in).getTime() - new Date(b.clock_in).getTime());
  };

  // Format military time (HH:MM) to 12-hour format
  const formatMilitaryTime = (timeStr: string) => {
    if (!timeStr) return '--:--';
    const [hours, minutes] = timeStr.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  // Get day name in selected language
  const getDayName = (dayName: string) => {
    const dayMap: Record<string, keyof typeof t> = {
      'Friday': 'friday',
      'Saturday': 'saturday',
      'Sunday': 'sunday',
      'Monday': 'monday',
      'Tuesday': 'tuesday',
      'Wednesday': 'wednesday',
      'Thursday': 'thursday'
    };
    return t[dayMap[dayName] || 'monday'] || dayName;
  };

  // Handle approve all
  const handleApproveAll = async () => {
    setLoading(true);
    try {
      await apiCall('/api/timecard-review/approve', 'POST', {
        week_start_date: weekStartDate
      });
      clearDraft();
      setStep('success');
      setTimeout(() => {
        onApprove();
      }, 2000);
    } catch (err) {
      setError('Failed to submit approval');
    } finally {
      setLoading(false);
    }
  };

  // Handle skip
  const handleSkip = async () => {
    setLoading(true);
    try {
      await apiCall('/api/timecard-review/skip', 'POST', {
        week_start_date: weekStartDate
      });
      clearDraft();
      onSkip();
    } catch (err) {
      // Still allow skip even if API fails
      onSkip();
    }
  };

  // Toggle entry selection
  const toggleEntrySelection = (entryId: number) => {
    setSelectedEntries(prev =>
      prev.includes(entryId)
        ? prev.filter(id => id !== entryId)
        : [...prev, entryId]
    );
  };

  // Days in the pay period that have NO time entry — the only valid choices for
  // "I missed a day". Excludes days already worked so a worker can't file a
  // missed-day on a date that already has an entry.
  const getMissingDays = () => {
    const entryDates = new Set(
      entries.map(e => e.entry_date?.split('T')[0]).filter(Boolean)
    );
    filedRequests
      .filter(r => r.correction_type === 'missed_day' && r.admin_status === 'pending' && r.proposed_date)
      .forEach(r => entryDates.add(String(r.proposed_date).split('T')[0]));
    const start = weekStartDate?.split('T')[0];
    const end = weekEndDate?.split('T')[0];
    const days: Array<{ value: string; label: string }> = [];
    if (!start || !end) return days;
    const cursor = new Date(start + 'T12:00:00');
    const last = new Date(end + 'T12:00:00');
    let guard = 0;
    while (cursor.getTime() <= last.getTime() && guard < 31) {
      const iso = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(cursor.getDate()).padStart(2, '0')}`;
      if (!entryDates.has(iso)) {
        days.push({
          value: iso,
          label: cursor.toLocaleDateString(language === 'es' ? 'es-US' : 'en-US', {
            weekday: 'long', month: 'short', day: 'numeric'
          })
        });
      }
      cursor.setDate(cursor.getDate() + 1);
      guard++;
    }
    return days;
  };

  // Configure the correction form for the screen at `index`. Pre-fills regular
  // entries with their ACTUAL recorded values (so an unchanged value is dropped
  // as a no-op) and rehydrates any issues already saved for that entry.
  const setupScreen = (index: number, screensArr: typeof screens, payload: Correction[]) => {
    const screen = screensArr[index];
    setError(null);
    if (!screen || screen.missed_day) {
      setIssueClockIn(false);
      setIssueClockOut(false);
      setIssueWrongJob(false);
      setIssueNoLunch(false);
      setProposedTime('07:00');
      setProposedClockOut('16:00');
      setProposedJobId(null);
      setProposedDate('');
      setNotes('');
      return;
    }
    const dayEs = dayEntriesFor(screen.entry_id);
    const firstE = dayEs[0];
    const lastE = dayEs[dayEs.length - 1];
    const dayIds = dayEs.map((e) => e.entry_id);
    const origIn = firstE ? isoToLocalHHMM(firstE.clock_in) : '';
    const origOut = lastE ? isoToLocalHHMM(lastE.clock_out) : '';
    const existing = payload.filter(c => c.entry_id != null && dayIds.includes(c.entry_id) && !c.auto_fix);
    const ci = existing.find(c => c.correction_type === 'wrong_clock_in');
    const co = existing.find(c => c.correction_type === 'wrong_clock_out');
    const wj = existing.find(c => c.correction_type === 'wrong_job');
    const nl = existing.find(c => c.correction_type === 'did_not_take_lunch');
    setIssueClockIn(!!ci);
    setIssueClockOut(!!co);
    setIssueWrongJob(!!wj);
    setIssueNoLunch(!!nl);
    setProposedTime(ci?.proposed_clock_in || origIn);
    setProposedClockOut(co?.proposed_clock_out || origOut);
    setProposedJobId(wj?.proposed_job_id ?? (firstE?.job_id ?? null));
    setProposedDate(firstE?.entry_date?.split('T')[0] || '');
    setNotes(existing[0]?.employee_notes || '');
  };

  // Build screen list (one per flagged entry + one for a missed day) and start.
  const handleContinueToCorrections = () => {
    const newScreens = [
      ...selectedEntries.map(entryId => ({ entry_id: entryId, missed_day: false })),
      ...(includeMissedDay ? [{ entry_id: null, missed_day: true }] : [])
    ];
    setScreens(newScreens);
    setCorrections([]);
    setCurrentScreenIndex(0);
    setupScreen(0, newScreens, []);
    setStep('correction');
  };

  // Helper to convert time to minutes for comparison
  const getTimeMinutes = (isoOrTime: string) => {
    if (!isoOrTime) return 0;
    let timeStr = isoOrTime;
    if (isoOrTime.includes('T')) {
      const date = new Date(isoOrTime);
      timeStr = `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    }
    const [h, m] = timeStr.split(':').map(Number);
    return h * 60 + m;
  };

  // Convert minutes to HH:MM format
  const minutesToTime = (mins: number) => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  };

  // Check for overlaps and return auto-fix corrections
  const getOverlapFixes = (entryId: number, newClockIn?: string, newClockOut?: string): Correction[] => {
    const currentEntry = entries.find(e => e.entry_id === entryId);
    if (!currentEntry) return [];

    const entryDate = currentEntry.entry_date?.split('T')[0];
    const proposedIn = newClockIn || currentEntry.clock_in;
    const proposedOut = newClockOut || currentEntry.clock_out;

    const newStart = getTimeMinutes(proposedIn);
    const newEnd = getTimeMinutes(proposedOut);

    const fixes: Correction[] = [];

    // Check other entries on the same day
    for (const other of entries) {
      if (other.entry_id === entryId) continue;

      const otherDate = other.entry_date?.split('T')[0];
      if (otherDate !== entryDate) continue;

      const otherStart = getTimeMinutes(other.clock_in);
      const otherEnd = getTimeMinutes(other.clock_out);

      // Check for overlap
      if (newStart < otherEnd && newEnd > otherStart) {
        // Determine how to fix:
        // If we're changing clock-in to earlier, the previous entry's clock-out needs to match
        // If we're changing clock-out to later, the next entry's clock-in needs to match
        if (newClockIn && newStart < otherEnd && otherStart < newStart) {
          // We moved clock-in earlier, other entry ends after our new start
          // Fix: set other entry's clock-out to our new clock-in
          fixes.push({
            entry_id: other.entry_id,
            correction_type: 'wrong_clock_out',
            proposed_clock_out: newClockIn,
            employee_notes: language === 'es'
              ? `Auto-ajustado para evitar superposición con otra entrada`
              : `Auto-adjusted to prevent overlap with another entry`
          });
        } else if (newClockOut && newEnd > otherStart && otherEnd > newEnd) {
          // We moved clock-out later, other entry starts before our new end
          // Fix: set other entry's clock-in to our new clock-out
          fixes.push({
            entry_id: other.entry_id,
            correction_type: 'wrong_clock_in',
            proposed_clock_in: newClockOut,
            employee_notes: language === 'es'
              ? `Auto-ajustado para evitar superposición con otra entrada`
              : `Auto-adjusted to prevent overlap with another entry`
          });
        }
      }
    }

    return fixes;
  };

  // Advance to the next screen, or to confirm/review when the wizard is done.
  const advanceScreen = (updatedPayload: Correction[]) => {
    const nextIndex = currentScreenIndex + 1;
    if (nextIndex < screens.length) {
      setCurrentScreenIndex(nextIndex);
      setupScreen(nextIndex, screens, updatedPayload);
    } else if (updatedPayload.length === 0) {
      // Every flagged entry turned out unchanged — nothing to submit. Send them
      // back to review so they can confirm all time is correct.
      setStep('review');
    } else {
      setStep('confirm');
    }
  };

  // "Nothing wrong with this day" — the worker flagged this entry by accident.
  // Drop any corrections recorded for it and move on (un-sticks the wizard).
  const skipCurrentScreen = () => {
    const screen = screens[currentScreenIndex];
    if (!screen) { setStep('confirm'); return; }
    const updated = screen.missed_day
      ? corrections.filter(c => c.entry_id !== null)
      : corrections.filter(c => c.entry_id !== screen.entry_id);
    setCorrections(updated);
    advanceScreen(updated);
  };

  // Save the current screen's corrections and move on. A regular entry can yield
  // several corrections (one per ticked issue); unchanged values are dropped.
  const saveCurrentScreen = () => {
    const screen = screens[currentScreenIndex];
    if (!screen) { setStep('confirm'); return; }

    // ---- Missed day ----
    if (screen.missed_day) {
      if (!proposedDate) { setError(t.selectDate); return; }
      if (!proposedJobId) { setError(t.selectJob); return; }
      if (!proposedTime) {
        setError(language === 'es' ? 'Por favor ingrese la hora de entrada' : 'Please enter the clock in time');
        return;
      }
      if (!proposedClockOut) {
        setError(language === 'es' ? 'Por favor ingrese la hora de salida' : 'Please enter the clock out time');
        return;
      }
      const updated = corrections.filter(c => c.entry_id !== null);
      updated.push({
        entry_id: null,
        correction_type: 'missed_day',
        proposed_clock_in: proposedTime,
        proposed_clock_out: proposedClockOut,
        proposed_job_id: proposedJobId || undefined,
        proposed_date: proposedDate,
        employee_notes: notes
      });
      setCorrections(updated);
      advanceScreen(updated);
      return;
    }

    // ---- Regular day (day-level: clock-in -> first event, clock-out -> last event) ----
    const dayEs = dayEntriesFor(screen.entry_id);
    const firstE = dayEs[0];
    const lastE = dayEs[dayEs.length - 1];
    if (!firstE) { advanceScreen(corrections); return; }
    const multiJob = dayEs.length > 1;

    if (!issueClockIn && !issueClockOut && !issueWrongJob && !issueNoLunch) {
      setError(t.pleaseSelectType);
      return;
    }
    if (issueClockIn && !proposedTime) {
      setError(language === 'es' ? 'Por favor ingrese la hora correcta de entrada' : 'Please enter the correct clock in time');
      return;
    }
    if (issueClockOut && !proposedClockOut) {
      setError(language === 'es' ? 'Por favor ingrese la hora correcta de salida' : 'Please enter the correct clock out time');
      return;
    }
    if (issueWrongJob && !proposedJobId) {
      setError(language === 'es' ? 'Por favor seleccione el trabajo correcto' : 'Please select the correct job');
      return;
    }

    const origIn = isoToLocalHHMM(firstE.clock_in);
    const origOut = isoToLocalHHMM(lastE.clock_out);

    // Build one correction per CHANGED issue (unchanged values are no-ops, dropped).
    const built: Correction[] = [];
    if (issueClockIn && proposedTime !== origIn) {
      built.push({ entry_id: firstE.entry_id, correction_type: 'wrong_clock_in', proposed_clock_in: proposedTime, employee_notes: notes });
    }
    if (issueClockOut && proposedClockOut !== origOut) {
      built.push({ entry_id: lastE.entry_id, correction_type: 'wrong_clock_out', proposed_clock_out: proposedClockOut, employee_notes: notes });
    }
    // Wrong-job only offered for a single-job day (a multi-job day is ambiguous).
    if (!multiJob && issueWrongJob && proposedJobId && proposedJobId !== firstE.job_id) {
      built.push({ entry_id: firstE.entry_id, correction_type: 'wrong_job', proposed_job_id: proposedJobId, employee_notes: notes });
    }
    if (issueNoLunch) {
      built.push({ entry_id: firstE.entry_id, correction_type: 'did_not_take_lunch', proposed_date: firstE.entry_date?.split('T')[0], employee_notes: notes });
    }

    // Day-level edits only move the day's outer bookends, so they can't create an
    // overlap — no auto-fixes needed (the backend overlap guard is the safety net).
    const dayIds = dayEs.map((e) => e.entry_id);
    const updated = corrections.filter(c => c.entry_id == null || !dayIds.includes(c.entry_id));
    updated.push(...built);

    setCorrections(updated);
    advanceScreen(updated);
  };

  // Use crew average for clock-out (ticks the clock-out issue and fills the value).
  const useCrewAverageTime = () => {
    setProposedClockOut(crewAverageClockOut);
    setIssueClockOut(true);
  };

  // Submit all corrections
  const handleSubmitCorrections = async () => {
    setLoading(true);
    try {
      // Get timezone offset
      const timezoneOffset = new Date().getTimezoneOffset();

      await apiCall('/api/timecard-review/corrections', 'POST', {
        week_start_date: weekStartDate,
        corrections: corrections.map(c => ({
          entry_id: c.entry_id,
          correction_type: c.correction_type,
          proposed_clock_in: c.proposed_clock_in,
          proposed_clock_out: c.proposed_clock_out,
          proposed_job_id: c.proposed_job_id,
          proposed_date: c.proposed_date,
          employee_notes: c.employee_notes
        })),
        timezone_offset_minutes: timezoneOffset
      });
      clearDraft();
      setStep('success');
    } catch (err: any) {
      // Surface the backend's message — e.g. the smart-AM/PM guard explains
      // exactly which time to fix ("clock-in would be after clock-out...").
      const serverMsg = err?.message && !/^API request failed/.test(err.message) ? err.message : null;
      setError(serverMsg || (language === 'es' ? 'No se pudieron enviar las correcciones' : 'Failed to submit corrections'));
    } finally {
      setLoading(false);
    }
  };

  // Get entry for the current screen
  const getCurrentEntry = () => {
    const screen = screens[currentScreenIndex];
    if (!screen || screen.missed_day || screen.entry_id == null) return null;
    return entries.find(e => e.entry_id === screen.entry_id) || null;
  };

  // Group entries by day
  const getEntriesGroupedByDay = () => {
    const groups: { [key: string]: { date: string; dayName: string; entries: TimeEntry[]; totalHours: number } } = {};

    for (const entry of entries) {
      const dateKey = entry.entry_date?.split('T')[0] || 'unknown';
      if (!groups[dateKey]) {
        groups[dateKey] = {
          date: entry.entry_date,
          dayName: entry.day_name,
          entries: [],
          totalHours: 0
        };
      }
      groups[dateKey].entries.push(entry);
      groups[dateKey].totalHours += entry.hours_worked || 0;
    }

    // Sort by date
    return Object.values(groups).sort((a, b) =>
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );
  };

  const groupedEntries = getEntriesGroupedByDay();

  // ---- Requests already on file for this week (My Time / earlier review) ----
  const reqStatus = (r: FiledRequest) => (r.admin_status === 'modified' ? 'approved' : r.admin_status);
  const requestsForDay = (group: { entries: TimeEntry[] }) => {
    const ids = new Set(group.entries.map(e => e.entry_id));
    return filedRequests.filter(r => r.entry_id != null && ids.has(r.entry_id));
  };
  // Pending or approved request on a day → the day can't be requested again
  // here (edit/withdraw lives in My Time). Denied → they may re-file.
  const dayLocked = (group: { entries: TimeEntry[] }) =>
    requestsForDay(group).some(r => reqStatus(r) === 'pending' || reqStatus(r) === 'approved');
  // Approved missed days already became real entries; show the rest.
  const missedRequests = filedRequests.filter(r => r.correction_type === 'missed_day' && reqStatus(r) !== 'approved');
  const describeRequest = (r: FiledRequest) => {
    const tm = (iso?: string | null) => (iso ? formatTime(iso) : '?');
    switch (r.correction_type) {
      case 'wrong_clock_in': return t.reqClockIn + ' → ' + tm(r.proposed_clock_in);
      case 'wrong_clock_out': return t.reqClockOut + ' → ' + tm(r.proposed_clock_out);
      case 'wrong_job': return t.reqJob + ' → ' + (r.proposed_job_name || ('#' + r.proposed_job_id));
      case 'did_not_take_lunch': return t.reqNoLunch;
      case 'missed_day': return t.reqMissed + ' ' + (r.proposed_date ? formatDate(String(r.proposed_date)) : '') + ' · ' + tm(r.proposed_clock_in) + ' - ' + tm(r.proposed_clock_out) + (r.proposed_job_name ? ' · ' + r.proposed_job_name : '');
      default: return r.correction_type;
    }
  };
  const renderRequestRow = (r: FiledRequest) => {
    const st = reqStatus(r);
    const chip = st === 'pending'
      ? { bg: '#fef3c7', fg: '#92400e', label: t.reqPending }
      : st === 'approved'
        ? { bg: '#dcfce7', fg: '#166534', label: t.reqApproved }
        : { bg: '#fee2e2', fg: '#991b1b', label: t.reqDenied };
    return (
      <div key={r.correction_id} style={{ display: 'flex', flexDirection: 'column', gap: '4px', padding: '8px 12px', borderTop: '1px dashed #e2e8f0', backgroundColor: '#fffbeb' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>{describeRequest(r)}</span>
          <span style={{ fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '4px', backgroundColor: chip.bg, color: chip.fg, whiteSpace: 'nowrap' }}>{chip.label}</span>
        </div>
        {r.employee_notes && <div style={{ fontSize: '12px', color: '#64748b' }}>“{r.employee_notes}”</div>}
        {st === 'rejected' && r.admin_notes && <div style={{ fontSize: '12px', color: '#991b1b' }}>{t.managerNote}: {r.admin_notes}</div>}
      </div>
    );
  };

  // Render based on current step
  const renderContent = () => {
    switch (step) {
      case 'review':
        return (
          <>
            {/* Total hours card */}
            <div style={styles.totalCard}>
              <div style={styles.totalLabel}>{t.totalHours}</div>
              <div style={styles.totalValue}>{totalHours} {t.hours}</div>
            </div>

            {/* Entry list grouped by day */}
            {entries.length === 0 ? (
              <div style={styles.noEntriesBox}>
                <Calendar size={48} color="#94a3b8" />
                <p style={{ marginTop: '12px' }}>{t.noEntries}</p>
              </div>
            ) : (
              groupedEntries.map(group => (
                <div key={group.date} style={{ marginBottom: '16px' }}>
                  {/* Day header with total */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 12px',
                    backgroundColor: '#f1f5f9',
                    borderRadius: '8px 8px 0 0',
                    borderBottom: '2px solid #e2e8f0'
                  }}>
                    <span style={{ fontWeight: '600', color: '#1e293b' }}>
                      {getDayName(group.dayName)} {formatDate(group.date)}
                    </span>
                    <span style={{
                      fontWeight: '600',
                      color: group.entries.length > 1 ? '#0369a1' : '#64748b',
                      fontSize: '14px'
                    }}>
                      {group.totalHours.toFixed(1)} {t.hours}
                    </span>
                  </div>

                  {/* Entries for this day */}
                  {group.entries.map((entry, idx) => (
                    <div key={entry.entry_id} style={{
                      ...styles.entryCard,
                      marginTop: 0,
                      borderRadius: idx === group.entries.length - 1 ? '0 0 8px 8px' : 0,
                      borderTop: idx > 0 ? '1px dashed #e2e8f0' : 'none'
                    }}>
                      <div style={styles.entryHeader}>
                        <span style={styles.entryDay}>{entry.job_name}</span>
                        <span style={styles.entryHours}>
                          {entry.hours_worked?.toFixed(1)} {t.hours}
                        </span>
                      </div>
                      <div style={styles.entryTimes}>
                        <span>
                          <Clock size={12} style={{ marginRight: '4px' }} />
                          {formatTime(entry.clock_in)} - {entry.clock_out ? formatTime(entry.clock_out) : t.noClockOut}
                        </span>
                      </div>
                      {entry.lunch_taken && (
                        <span style={styles.lunchBadge}>
                          🍽️ {entry.lunch_minutes || 30} min {t.lunchDeducted}
                        </span>
                      )}
                      {entry.needs_review && !entry.has_pending_correction && (
                        <span style={styles.flagBadge}>
                          <AlertTriangle size={12} />
                          {t.flaggedForReview}
                        </span>
                      )}
                    </div>
                  ))}
                  {requestsForDay(group).map(renderRequestRow)}
                </div>
              ))
            )}

            {/* Missed-day requests on file (pending / denied) */}
            {missedRequests.length > 0 && (
              <div style={{ marginBottom: '16px', border: '1px solid #fde68a', borderRadius: '8px', overflow: 'hidden' }}>
                <div style={{ padding: '8px 12px', backgroundColor: '#fef3c7', fontWeight: 600, color: '#92400e', fontSize: '13px' }}>{t.filedTitle}</div>
                {missedRequests.map(renderRequestRow)}
              </div>
            )}
          </>
        );

      case 'selectIssues':
        return (
          <>
            <p style={{ color: '#64748b', marginBottom: '16px', fontSize: '14px' }}>
              {t.selectAtLeastOne}
            </p>

            {groupedEntries.map(group => (
              <div key={group.date} style={{ marginBottom: '12px' }}>
                {/* Day header */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '6px 10px',
                  backgroundColor: '#f1f5f9',
                  borderRadius: '6px',
                  marginBottom: '4px'
                }}>
                  <span style={{ fontWeight: '600', color: '#1e293b', fontSize: '13px' }}>
                    {getDayName(group.dayName)} {formatDate(group.date)}
                  </span>
                  <span style={{
                    fontWeight: '600',
                    color: group.entries.length > 1 ? '#0369a1' : '#64748b',
                    fontSize: '13px'
                  }}>
                    {group.totalHours.toFixed(1)} {t.hours}
                  </span>
                </div>

                {/* One card per day (the worker fixes the day's overall in/out) */}
                {(() => {
                  const dayEntries = group.entries;
                  const firstE = dayEntries[0];
                  const lastE = dayEntries[dayEntries.length - 1];
                  const isSelected = selectedEntries.includes(firstE.entry_id);
                  const jobNames = Array.from(new Set(dayEntries.map(e => e.job_name)));
                  const anyLunch = dayEntries.some(e => e.lunch_taken);
                  const locked = dayLocked(group);
                  return (
                    <div
                      style={{
                        ...styles.checkboxCard,
                        ...(isSelected ? styles.checkboxCardSelected : {}),
                        marginBottom: '4px',
                        ...(locked ? { opacity: 0.6, cursor: 'default' } : {})
                      }}
                      onClick={() => { if (!locked) toggleEntrySelection(firstE.entry_id); }}
                    >
                      <div style={{ ...styles.checkbox, ...(isSelected ? styles.checkboxChecked : {}) }}>
                        {isSelected && <CheckCircle size={16} />}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '14px', color: '#0369a1', fontWeight: '500' }}>
                          {jobNames.join(', ')}
                        </div>
                        <div style={{ fontSize: '13px', color: '#64748b' }}>
                          {formatTime(firstE.clock_in)} - {formatTime(lastE.clock_out)} • {group.totalHours.toFixed(1)} {t.hours}
                          {dayEntries.length > 1 && (
                            <span style={{ marginLeft: '8px', color: '#0369a1' }}>· {dayEntries.length} jobs</span>
                          )}
                          {anyLunch && (
                            <span style={{ marginLeft: '8px', color: '#f59e0b' }}>🍽️</span>
                          )}
                        </div>
                        {locked && (
                          <div style={{ fontSize: '12px', color: '#92400e', marginTop: '4px', fontWeight: 600 }}>{t.reqAlready}</div>
                        )}
                      </div>
                    </div>
                  );
                })()}
                {/* What they already asked for on this day, with the office's answer */}
                {requestsForDay(group).length > 0 && (
                  <div style={{ border: '1px solid #fde68a', borderRadius: '6px', overflow: 'hidden', marginTop: '2px' }}>
                    {requestsForDay(group).map(renderRequestRow)}
                  </div>
                )}
              </div>
            ))}

            {/* Missed day option */}
            <div
              style={{
                ...styles.checkboxCard,
                ...(includeMissedDay ? styles.checkboxCardSelected : {}),
                marginTop: '16px',
                borderStyle: 'dashed'
              }}
              onClick={() => setIncludeMissedDay(!includeMissedDay)}
            >
              <div style={{
                ...styles.checkbox,
                ...(includeMissedDay ? styles.checkboxChecked : {})
              }}>
                {includeMissedDay && <CheckCircle size={16} />}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '600', color: '#1e293b' }}>
                  {t.missedDayOption}
                </div>
              </div>
            </div>

          </>
        );

      case 'correction': {
        const screen = screens[currentScreenIndex];
        const currentEntry = getCurrentEntry();
        const isMissedDay = !!screen?.missed_day;
        const dateKey = currentEntry?.entry_date?.split('T')[0];
        const canNoLunch = !!(isAsphaltCrew && currentEntry &&
          entries.some(e => e.lunch_taken && e.entry_date?.split('T')[0] === dateKey));
        const missingDays = isMissedDay ? getMissingDays() : [];
        // Day-level view: this day's entries, with first/last for the bookends.
        const dayEs = (screen && !isMissedDay) ? dayEntriesFor(screen.entry_id) : [];
        const dayFirst = dayEs[0];
        const dayLast = dayEs[dayEs.length - 1];
        const multiJob = dayEs.length > 1;

        // One row of the "check all that apply" issue list.
        const issueRow = (checked: boolean, onToggle: () => void, label: string) => (
          <div
            style={{ ...styles.checkboxCard, ...(checked ? styles.checkboxCardSelected : {}), marginBottom: '8px' }}
            onClick={onToggle}
          >
            <div style={{ ...styles.checkbox, ...(checked ? styles.checkboxChecked : {}) }}>
              {checked && <CheckCircle size={16} />}
            </div>
            <div style={{ flex: 1, fontWeight: 600, color: '#1e293b' }}>{label}</div>
          </div>
        );

        return (
          <>
            <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '13px', color: '#64748b' }}>
                {currentScreenIndex + 1} / {screens.length}
              </span>
            </div>

            {currentEntry && (
              <>
                {/* Date and Day Header */}
                <div style={{
                  backgroundColor: '#0369a1',
                  color: 'white',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  marginBottom: '12px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '18px', fontWeight: '700' }}>
                    {getDayName(currentEntry.day_name)}
                  </div>
                  <div style={{ fontSize: '14px', opacity: 0.9 }}>
                    {formatDate(currentEntry.entry_date)}
                  </div>
                </div>
                <div style={styles.currentValueBox}>
                  <div style={styles.currentValueLabel}>{t.currentValue}</div>
                  <div style={styles.currentValueText}>
                    {multiJob
                      ? `${Array.from(new Set(dayEs.map(e => e.job_name))).join(', ')} • ${formatTime(dayFirst?.clock_in)} - ${formatTime(dayLast?.clock_out)}`
                      : `${currentEntry.job_name} • ${formatTime(currentEntry.clock_in)} - ${formatTime(currentEntry.clock_out)}`}
                  </div>
                </div>
              </>
            )}

            {!isMissedDay && currentEntry && (
              <>
                <label style={styles.label}>
                  {t.whatIsWrong}{' '}
                  <span style={{ fontWeight: 400, color: '#94a3b8' }}>
                    ({language === 'es' ? 'marque todo lo que aplique' : 'check all that apply'})
                  </span>
                </label>

                {/* Wrong clock-in */}
                {issueRow(issueClockIn, () => setIssueClockIn(v => !v), t.wrongClockIn)}
                {issueClockIn && (
                  <div style={{ ...styles.formGroup, marginTop: '-2px' }}>
                    <CustomTimePicker
                      value={proposedTime}
                      onChange={(val) => setProposedTime(val)}
                      label={language === 'es' ? 'Entrada' : 'Clock In'}
                    />
                  </div>
                )}

                {/* Wrong clock-out */}
                {issueRow(issueClockOut, () => setIssueClockOut(v => !v), t.wrongClockOut)}
                {issueClockOut && (
                  <div style={{ ...styles.formGroup, marginTop: '-2px' }}>
                    <button style={styles.crewAverageButton} onClick={useCrewAverageTime}>
                      <Users size={18} />
                      {t.useCrewAverage}: {formatMilitaryTime(crewAverageClockOut)}
                    </button>
                    <div style={styles.orDivider}>— {t.enterManually} —</div>
                    <CustomTimePicker
                      value={proposedClockOut}
                      onChange={(val) => setProposedClockOut(val)}
                      label={language === 'es' ? 'Salida' : 'Clock Out'}
                    />
                  </div>
                )}

                {/* Wrong job — only for a single-job day (multi-job day is ambiguous) */}
                {!multiJob && issueRow(issueWrongJob, () => setIssueWrongJob(v => !v), t.wrongJob)}
                {!multiJob && issueWrongJob && (
                  <div style={{ ...styles.formGroup, marginTop: '-2px' }}>
                    <CustomSelect
                      value={proposedJobId || ''}
                      onChange={(value) => setProposedJobId(Number(value))}
                      placeholder={t.selectJob}
                      options={jobs.map(job => ({
                        value: job.job_id,
                        label: `${job.job_name} (${job.bradanna_job_number})`
                      }))}
                    />
                  </div>
                )}

                {/* Did not take lunch (asphalt crew only) */}
                {canNoLunch && issueRow(issueNoLunch, () => setIssueNoLunch(v => !v), `🍽️ ${t.noLunchOption}`)}
                {issueNoLunch && (
                  <div style={{
                    padding: '12px 16px',
                    backgroundColor: '#dcfce7',
                    borderRadius: '8px',
                    fontSize: '14px',
                    color: '#166534',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '12px'
                  }}>
                    <span>🍽️</span>
                    {language === 'es'
                      ? 'Se agregarán 30 minutos a sus horas para este día'
                      : '30 minutes will be added to your hours for this day'}
                  </div>
                )}
              </>
            )}

            {isMissedDay && (
              <>
                {/* Show selected date header for missed day */}
                {proposedDate && (
                  <div style={{
                    backgroundColor: '#0369a1',
                    color: 'white',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    marginBottom: '12px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '18px', fontWeight: '700' }}>
                      {new Date(proposedDate + 'T12:00:00').toLocaleDateString(language === 'es' ? 'es-US' : 'en-US', { weekday: 'long' })}
                    </div>
                    <div style={{ fontSize: '14px', opacity: 0.9 }}>
                      {new Date(proposedDate + 'T12:00:00').toLocaleDateString(language === 'es' ? 'es-US' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </div>
                  </div>
                )}
                <div style={styles.formGroup}>
                  <label style={styles.label}>{t.selectDate}</label>
                  {missingDays.length === 0 ? (
                    <div style={{ fontSize: '14px', color: '#92400e', padding: '12px', backgroundColor: '#fef3c7', borderRadius: '8px' }}>
                      {language === 'es'
                        ? 'No hay días sin registro en este período.'
                        : 'No days without an entry in this pay period.'}
                    </div>
                  ) : (
                    <CustomSelect
                      value={proposedDate}
                      onChange={(value) => setProposedDate(String(value))}
                      placeholder={t.selectDate}
                      options={missingDays}
                    />
                  )}
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>{t.selectJob}</label>
                  <CustomSelect
                    value={proposedJobId || ''}
                    onChange={(value) => setProposedJobId(Number(value))}
                    placeholder={t.selectJob}
                    options={jobs.map(job => ({
                      value: job.job_id,
                      label: job.job_name
                    }))}
                  />
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <div style={{ ...styles.formGroup, flex: 1 }}>
                    <label style={styles.label}>{language === 'es' ? 'Entrada' : 'Clock In'}</label>
                    <CustomTimePicker
                      value={proposedTime}
                      onChange={(val) => setProposedTime(val)}
                      label={language === 'es' ? 'Entrada' : 'Clock In'}
                    />
                  </div>
                  <div style={{ ...styles.formGroup, flex: 1 }}>
                    <label style={styles.label}>{language === 'es' ? 'Salida' : 'Clock Out'}</label>
                    <CustomTimePicker
                      value={proposedClockOut}
                      onChange={(val) => setProposedClockOut(val)}
                      label={language === 'es' ? 'Salida' : 'Clock Out'}
                    />
                  </div>
                </div>
                <div style={{
                  padding: '10px 12px',
                  backgroundColor: '#fef3c7',
                  borderRadius: '6px',
                  fontSize: '13px',
                  color: '#92400e',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span>🍽️</span>
                  {language === 'es'
                    ? '30 min de almuerzo incluidos si más de 6 horas'
                    : '30 min lunch included if over 6 hours'
                  }
                </div>
              </>
            )}

            <div style={styles.formGroup}>
              <label style={styles.label}>{t.addNotes}</label>
              <textarea
                style={styles.textarea as any}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t.notesPlaceholder}
              />
            </div>
          </>
        );
      }

      case 'confirm':
        // Sort corrections by date
        const sortedCorrections = [...corrections].sort((a, b) => {
          const entryA = a.entry_id ? entries.find(e => e.entry_id === a.entry_id) : null;
          const entryB = b.entry_id ? entries.find(e => e.entry_id === b.entry_id) : null;
          const dateA = entryA?.entry_date || a.proposed_date || '';
          const dateB = entryB?.entry_date || b.proposed_date || '';
          return dateA.localeCompare(dateB);
        });

        return (
          <>
            <p style={{ color: '#64748b', marginBottom: '16px' }}>
              {t.correctionsToSubmit}: {sortedCorrections.length}
            </p>

            {sortedCorrections.map((correction, index) => {
              const entry = correction.entry_id
                ? entries.find(e => e.entry_id === correction.entry_id)
                : null;
              const isMissedDayType = correction.correction_type === 'missed_day';
              const isNoLunchType = correction.correction_type === 'did_not_take_lunch';
              const selectedJob = jobs.find(j => j.job_id === correction.proposed_job_id);

              // For no lunch correction, find the entry on that date
              const noLunchEntry = isNoLunchType && correction.proposed_date
                ? entries.find(e => e.entry_date?.split('T')[0] === correction.proposed_date && e.lunch_taken)
                : null;

              return (
                <div key={index} style={{
                  ...styles.summaryCard,
                  ...(correction.auto_fix ? { borderLeft: '4px solid #8b5cf6' } : {}),
                  ...(isNoLunchType ? { borderLeft: '4px solid #f59e0b' } : {})
                }}>
                  <div style={{ fontWeight: '600', marginBottom: '8px', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {entry
                      ? `${getDayName(entry.day_name)} ${formatDate(entry.entry_date)}`
                      : isMissedDayType && correction.proposed_date
                        ? `${t.missedDay}: ${formatDate(correction.proposed_date)}`
                        : isNoLunchType && correction.proposed_date
                          ? `🍽️ ${formatDate(correction.proposed_date)}`
                          : t.missedDay
                    }
                    {correction.auto_fix && (
                      <span style={{
                        fontSize: '10px',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        backgroundColor: '#8b5cf6',
                        color: 'white',
                        fontWeight: '600'
                      }}>
                        {language === 'es' ? 'AUTO' : 'AUTO-FIX'}
                      </span>
                    )}
                  </div>
                  <div style={styles.summaryRow}>
                    <span style={styles.summaryLabel}>Type</span>
                    <span style={styles.summaryValue}>
                      {correction.correction_type === 'wrong_clock_in' && t.wrongClockIn}
                      {correction.correction_type === 'wrong_clock_out' && t.wrongClockOut}
                      {correction.correction_type === 'wrong_job' && t.wrongJob}
                      {correction.correction_type === 'missed_day' && t.missedDay}
                      {correction.correction_type === 'did_not_take_lunch' && t.didNotTakeLunch}
                    </span>
                  </div>
                  {isMissedDayType && selectedJob && (
                    <div style={styles.summaryRow}>
                      <span style={styles.summaryLabel}>{t.selectJob}</span>
                      <span style={styles.summaryValue}>{selectedJob.job_name}</span>
                    </div>
                  )}
                  {isMissedDayType && correction.proposed_clock_in && correction.proposed_clock_out && (
                    <div style={styles.summaryRow}>
                      <span style={styles.summaryLabel}>{language === 'es' ? 'Horario' : 'Time'}</span>
                      <span style={{ ...styles.summaryValue, color: '#10b981' }}>
                        {formatMilitaryTime(correction.proposed_clock_in)} - {formatMilitaryTime(correction.proposed_clock_out)}
                      </span>
                    </div>
                  )}
                  {isNoLunchType && (
                    <div style={styles.summaryRow}>
                      <span style={styles.summaryLabel}>{t.proposed}</span>
                      <span style={{ ...styles.summaryValue, color: '#10b981' }}>
                        +30 min
                      </span>
                    </div>
                  )}
                  {!isMissedDayType && !isNoLunchType && correction.proposed_clock_out && (
                    <div style={styles.summaryRow}>
                      <span style={styles.summaryLabel}>{t.proposed}</span>
                      <span style={{ ...styles.summaryValue, color: '#10b981' }}>
                        {formatMilitaryTime(correction.proposed_clock_out)}
                      </span>
                    </div>
                  )}
                  {!isMissedDayType && !isNoLunchType && correction.proposed_clock_in && (
                    <div style={styles.summaryRow}>
                      <span style={styles.summaryLabel}>{t.proposed}</span>
                      <span style={{ ...styles.summaryValue, color: '#10b981' }}>
                        {formatMilitaryTime(correction.proposed_clock_in)}
                      </span>
                    </div>
                  )}
                  {correction.employee_notes && (
                    <div style={{ fontSize: '13px', color: '#64748b', marginTop: '8px' }}>
                      <strong>{t.yourNote}:</strong> {correction.employee_notes}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        );

      case 'success':
        return (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={styles.successIcon}>
              <CheckCircle size={40} color="#10b981" />
            </div>
            <h3 style={{ fontSize: '20px', marginBottom: '12px', color: '#1e293b' }}>
              {t.successTitle}
            </h3>
            <p style={{ color: '#64748b' }}>
              {corrections.length > 0 ? t.correctionsSubmitted : t.timeApproved}
            </p>
          </div>
        );
    }
  };

  // Render footer buttons based on step
  const renderFooter = () => {
    switch (step) {
      case 'review':
        return (
          <>
            <button
              style={styles.buttonPrimary}
              onClick={handleApproveAll}
              disabled={loading}
            >
              <CheckCircle size={20} />
              {t.allTimeCorrect}
            </button>
            <button
              style={styles.buttonSecondary}
              onClick={() => setStep('selectIssues')}
            >
              <AlertTriangle size={20} />
              {t.somethingWrong}
            </button>
            {canSkip && (
              <button
                style={styles.buttonSkip}
                onClick={handleSkip}
                disabled={loading}
              >
                {t.skipForNow}
              </button>
            )}
          </>
        );

      case 'selectIssues':
        return (
          <>
            <button
              style={{
                ...styles.buttonPrimary,
                opacity: (selectedEntries.length === 0 && !includeMissedDay) ? 0.5 : 1
              }}
              onClick={handleContinueToCorrections}
              disabled={selectedEntries.length === 0 && !includeMissedDay}
            >
              {t.continue}
              <ChevronRight size={20} />
            </button>
            <button
              style={styles.buttonSkip}
              onClick={() => setStep('review')}
            >
              {t.back}
            </button>
          </>
        );

      case 'correction': {
        const corrScreen = screens[currentScreenIndex];
        const corrIsMissedDay = !!corrScreen?.missed_day;
        return (
          <>
            <button
              style={styles.buttonPrimary}
              onClick={saveCurrentScreen}
            >
              {currentScreenIndex < screens.length - 1 ? t.next : t.continue}
              <ChevronRight size={20} />
            </button>
            <button
              style={{
                width: '100%',
                padding: '14px',
                backgroundColor: 'white',
                color: '#475569',
                border: '2px solid #cbd5e1',
                borderRadius: '12px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                marginBottom: '10px'
              }}
              onClick={skipCurrentScreen}
            >
              <CheckCircle size={18} color="#10b981" />
              {corrIsMissedDay ? t.noMissedDay : t.nothingWrong}
            </button>
            <button
              style={styles.buttonSkip}
              onClick={() => {
                if (currentScreenIndex > 0) {
                  const prev = currentScreenIndex - 1;
                  setCurrentScreenIndex(prev);
                  setupScreen(prev, screens, corrections);
                } else {
                  setStep('selectIssues');
                }
              }}
            >
              {t.back}
            </button>
          </>
        );
      }

      case 'confirm':
        return (
          <>
            <button
              style={styles.buttonPrimary}
              onClick={handleSubmitCorrections}
              disabled={loading}
            >
              {loading ? '...' : t.submit}
            </button>
            <button
              style={styles.buttonSkip}
              onClick={() => {
                const last = Math.max(0, screens.length - 1);
                setCurrentScreenIndex(last);
                setupScreen(last, screens, corrections);
                setStep('correction');
              }}
            >
              {t.back}
            </button>
          </>
        );

      case 'success':
        return (
          <button
            style={styles.buttonPrimary}
            onClick={onApprove}
          >
            {language === 'es' ? 'Fichar Entrada' : 'Clock In'}
          </button>
        );
    }
  };

  // Get header title based on step
  const getHeaderTitle = () => {
    switch (step) {
      case 'review': return t.reviewTitle;
      case 'selectIssues': return t.selectIssuesTitle;
      case 'correction': return t.correctionTitle;
      case 'confirm': return t.confirmTitle;
      case 'success': return t.successTitle;
    }
  };

  const getHeaderSubtitle = () => {
    switch (step) {
      case 'review': return t.reviewSubtitle;
      case 'selectIssues': return t.selectIssuesSubtitle;
      default: return null;
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        {/* Header */}
        <div style={styles.header}>
          <h2 style={styles.headerTitle}>{getHeaderTitle()}</h2>
          {getHeaderSubtitle() && (
            <p style={styles.headerSubtitle}>{getHeaderSubtitle()}</p>
          )}
          {step === 'review' && (
            <div style={styles.weekBadge}>
              <Calendar size={14} />
              {t.weekOf} {formatDate(weekStartDate)} - {formatDate(weekEndDate)}
            </div>
          )}
        </div>

        {/* Content */}
        <div style={styles.content}>
          {error && (
            <div
              onClick={() => setError(null)}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10002,
                padding: '20px'
              }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  padding: '28px',
                  maxWidth: '360px',
                  width: '100%',
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                  <AlertTriangle size={48} color="#dc2626" />
                  <p style={{ color: '#374151', margin: 0, fontSize: '17px', textAlign: 'center', lineHeight: '1.5', fontWeight: '500' }}>
                    {error}
                  </p>
                  <button
                    onClick={() => setError(null)}
                    style={{
                      width: '100%',
                      padding: '14px',
                      fontSize: '17px',
                      fontWeight: '700',
                      color: 'white',
                      backgroundColor: '#dc2626',
                      border: 'none',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      marginTop: '4px'
                    }}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          )}
          {renderContent()}
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          {renderFooter()}
        </div>
      </div>
    </div>
  );
};

export default TimecardReviewModal;
